import { PageController } from '@client/components/_core/layout/PageController';
import { AdminRoleSelector } from '@client/components/_shared/selectors/users/AdminRoleSelector';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import type { RouterOutputs } from '@client/trpc/client';
import { SYSTEM_ORG_IDS } from '@shared/constants/business/org';
import { ADMIN_ROLES, type AdminRole } from '@shared/constants/business/user';
import { AUTH0_CONNECTIONS, type Auth0Connection } from '@shared/constants/system/auth0';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Avatar, Divider, Drawer, Empty, Form, Input, List, Select, Tabs, Typography } from 'antd';
import { ArrowLeft, Mail, Search, User, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/admin-console/user-access/admin-users/grant')({
    staticData: {
        presentation: 'overlay',
    },
    component: GrantAdminAccessRoute,
});

const authConnectionOptions = [
    { label: 'Username & Password', value: AUTH0_CONNECTIONS.DATABASE.name },
    { label: 'Microsoft Azure AD (SSO)', value: AUTH0_CONNECTIONS.AZURE_AD.name },
];

type Mode = 'existing' | 'invite';

type InviteFormValues = {
    email: string;
    firstName: string;
    lastName: string;
    allowedAuthMethods: Auth0Connection[];
};

function GrantAdminAccessRoute() {
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [emailFailure, setEmailFailure] = useState<RouterOutputs['admin']['adminRole']['invite']>();
    const [mode, setMode] = useState<Mode>('existing');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState<{
        id: bigint;
        name: string;
        email: string;
        photoUrl?: string;
    } | null>(null);
    const [selectedRoles, setSelectedRoles] = useState<AdminRole[]>([ADMIN_ROLES.SUPER_ADMIN]);
    const [form] = Form.useForm<InviteFormValues>();

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Search for users who don't have admin access (no OrgUser in SYSTEM_CONSOLE)
    const { data: users, isFetching } = trpc.admin.user.listUsers.useQuery(
        {
            where: {
                // Exclude users who already have OrgUser in SYSTEM_CONSOLE
                NOT: {
                    OrgUsers: {
                        some: {
                            orgId: SYSTEM_ORG_IDS.SYSTEM_CONSOLE,
                            disabled: false,
                        },
                    },
                },
                ...(debouncedSearch
                    ? {
                          OR: [
                              { firstName: { contains: debouncedSearch, mode: 'insensitive' } },
                              { lastName: { contains: debouncedSearch, mode: 'insensitive' } },
                              { email: { contains: debouncedSearch, mode: 'insensitive' } },
                          ],
                      }
                    : {}),
            },
            limit: 20,
        },
        { enabled: open && mode === 'existing' && !selectedUser },
    );

    const { mutate: grantAccess, isPending: isGranting } = trpc.admin.adminRole.grantAccess.useMutation();
    const { mutate: inviteAdmin, isPending: isInviting } = trpc.admin.adminRole.invite.useMutation();

    const resend = trpc.admin.user.resendUserInvite.useMutation({
        onSuccess: () => {
            message.success('Invite sent successfully.');
            setEmailFailure(undefined);
            handleClose();
        },
        onError: (error) => {
            if (error.data?.errorId === 'invite.emailSendFailed') {
                errorMessage.show(error.message);
                return;
            }
            errorMessage.show(error);
        },
    });

    const handleClose = () => {
        setOpen(false);
    };

    const handleAfterOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            void trpcUtils.admin.adminRole.list.invalidate();
            navigate({ to: '/admin-console/user-access/admin-users' });
        }
    };

    const handleSelectUser = (user: {
        id: bigint;
        firstName: string | null;
        lastName: string | null;
        email: string;
        photoUrl?: string | null;
    }) => {
        setSelectedUser({
            id: user.id,
            name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Unknown',
            email: user.email,
            photoUrl: user.photoUrl ?? undefined,
        });
    };

    const handleBack = () => {
        setSelectedUser(null);
        setSelectedRoles([ADMIN_ROLES.SUPER_ADMIN]);
    };

    const handleGrantAccess = () => {
        if (!selectedUser || selectedRoles.length === 0) return;

        grantAccess(
            { userId: selectedUser.id, adminRoles: selectedRoles },
            {
                onSuccess: () => {
                    message.success('Admin access granted successfully');
                    handleClose();
                },
                onError: (err) => errorMessage.show(err),
            },
        );
    };

    const handleInvite = () => {
        form.validateFields().then((values) => {
            if (selectedRoles.length === 0) {
                errorMessage.show('Please select at least one admin role');
                return;
            }

            inviteAdmin(
                {
                    email: values.email,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    allowedAuthMethods: values.allowedAuthMethods,
                    adminRoles: selectedRoles,
                },
                {
                    onSuccess: (result) => {
                        void trpcUtils.admin.adminRole.list.invalidate();
                        if (result.emailStatus === 'failed') {
                            setEmailFailure(result);
                            return;
                        }
                        message.success(result.message);
                        handleClose();
                    },
                    onError: (err) => errorMessage.show(err),
                },
            );
        });
    };

    const handleModeChange = (newMode: string) => {
        setMode(newMode as Mode);
        setSelectedUser(null);
        setSelectedRoles([ADMIN_ROLES.SUPER_ADMIN]);
        setSearchTerm('');
        setDebouncedSearch('');
        form.resetFields();
    };

    const renderExistingUserMode = () => {
        if (!selectedUser) {
            return (
                <>
                    <Typography.Text type="secondary" className="mb-4 block">
                        Find an existing Curium user and assign the admin roles they need.
                    </Typography.Text>

                    <Input
                        placeholder="Search users by name or email"
                        prefix={<Search className="h-4 w-4 text-subtle-foreground" />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                        autoFocus
                    />

                    <List
                        className="mt-4 max-h-[60vh] overflow-y-auto"
                        dataSource={users?.rows ?? []}
                        loading={isFetching}
                        locale={{
                            emptyText: (
                                <Empty
                                    description={
                                        debouncedSearch ? 'No users match that search' : 'Start typing to search users'
                                    }
                                    className="py-8"
                                />
                            ),
                        }}
                        renderItem={(user) => (
                            <List.Item
                                key={user.id.toString()}
                                className="cursor-pointer rounded px-2 transition hover:bg-muted"
                                onClick={() => handleSelectUser(user)}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar src={user.photoUrl} icon={<User className="h-4 w-4" />} size={40} />
                                    }
                                    title={
                                        <span className="font-medium">
                                            {user.firstName} {user.lastName}
                                        </span>
                                    }
                                    description={<Typography.Text type="secondary">{user.email}</Typography.Text>}
                                />
                            </List.Item>
                        )}
                    />
                </>
            );
        }

        return (
            <>
                <Button
                    type="tertiary"
                    icon={<ArrowLeft />}
                    onClick={handleBack}
                    className="mb-4 -ml-2 text-subtle-foreground"
                >
                    Back to search
                </Button>

                <div className="mb-6 flex items-center gap-3 rounded-lg border border-separator bg-muted p-4">
                    <Avatar src={selectedUser.photoUrl} icon={<User className="h-4 w-4" />} size={48} />
                    <div>
                        <Typography.Text strong className="block">
                            {selectedUser.name}
                        </Typography.Text>
                        <Typography.Text type="secondary">{selectedUser.email}</Typography.Text>
                    </div>
                </div>

                <AdminRoleSelector
                    selectedRoles={selectedRoles}
                    onChange={setSelectedRoles}
                    description="Combine roles to match what this admin should control. You can adjust later."
                />
            </>
        );
    };

    const renderInviteMode = () => {
        return (
            <>
                <Typography.Text type="secondary" className="mb-4 block">
                    We'll email them setup instructions and apply the roles you choose.
                </Typography.Text>

                <Form<InviteFormValues>
                    form={form}
                    layout="vertical"
                    initialValues={{
                        allowedAuthMethods: [AUTH0_CONNECTIONS.DATABASE.name, AUTH0_CONNECTIONS.AZURE_AD.name],
                    }}
                >
                    <Form.Item<InviteFormValues>
                        name="email"
                        label="Work email"
                        rules={[
                            { required: true, message: 'Please enter an email address' },
                            { type: 'email', message: 'Please enter a valid email address' },
                        ]}
                    >
                        <Input
                            prefix={<Mail className="h-4 w-4 text-subtle-foreground" />}
                            placeholder="admin@example.com"
                        />
                    </Form.Item>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item<InviteFormValues>
                            name="firstName"
                            label="First name"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Input placeholder="Jordan" />
                        </Form.Item>

                        <Form.Item<InviteFormValues>
                            name="lastName"
                            label="Last name"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Input placeholder="Smith" />
                        </Form.Item>
                    </div>

                    <Form.Item<InviteFormValues>
                        name="allowedAuthMethods"
                        label="Authentication methods"
                        tooltip="Select which authentication methods this user can use to sign in."
                        rules={[{ required: true, message: 'Please select at least one authentication method' }]}
                    >
                        <Select mode="multiple" options={authConnectionOptions} />
                    </Form.Item>
                </Form>

                <Divider />

                <AdminRoleSelector
                    selectedRoles={selectedRoles}
                    onChange={setSelectedRoles}
                    description="Combine roles to match what this admin should control. You can adjust later."
                />
            </>
        );
    };

    const canSubmit =
        selectedRoles.length > 0 && (mode === 'existing' ? selectedUser !== null : form.getFieldsError().length === 0);

    return (
        <PageController route={Route}>
            <Drawer
                title={
                    <Space>
                        <UserPlus className="h-5 w-5" />
                        <span>Add admin user</span>
                    </Space>
                }
                open={open}
                onClose={handleClose}
                afterOpenChange={handleAfterOpenChange}
                width={520}
                footer={
                    emailFailure ? (
                        <Space className="flex justify-end">
                            <Button type="secondary" onClick={handleClose}>
                                Close
                            </Button>
                            {emailFailure.canResendInvite && (
                                <Button
                                    type="primary"
                                    loading={resend.isPending}
                                    onClick={() =>
                                        resend.mutate({
                                            userId: emailFailure.userId,
                                            orgId: SYSTEM_ORG_IDS.SYSTEM_CONSOLE,
                                        })
                                    }
                                >
                                    Retry resend
                                </Button>
                            )}
                        </Space>
                    ) : mode === 'existing' && selectedUser ? (
                        <Space className="flex justify-end">
                            <Button type="secondary" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleGrantAccess}
                                loading={isGranting}
                                disabled={!canSubmit}
                            >
                                Grant admin access
                            </Button>
                        </Space>
                    ) : mode === 'invite' ? (
                        <Space className="flex justify-end">
                            <Button type="secondary" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleInvite}
                                loading={isInviting}
                                disabled={selectedRoles.length === 0}
                            >
                                Send invite
                            </Button>
                        </Space>
                    ) : null
                }
            >
                {emailFailure ? (
                    <Alert
                        type="warning"
                        showIcon
                        title="Admin access added, but the email failed"
                        description={
                            emailFailure.canResendInvite
                                ? 'Admin access is saved. Retry resend to send the invitation without granting access again.'
                                : 'Admin access is saved. This user can sign in with their existing account. The notification email could not be sent.'
                        }
                    />
                ) : (
                    <Tabs
                        activeKey={mode}
                        onChange={handleModeChange}
                        items={[
                            {
                                key: 'existing',
                                label: (
                                    <span className="flex items-center gap-2">
                                        <User className="h-4 w-4" />
                                        Existing user
                                    </span>
                                ),
                                children: renderExistingUserMode(),
                            },
                            {
                                key: 'invite',
                                label: (
                                    <span className="flex items-center gap-2">
                                        <UserPlus className="h-4 w-4" />
                                        Invite new
                                    </span>
                                ),
                                children: renderInviteMode(),
                            },
                        ]}
                    />
                )}
            </Drawer>
        </PageController>
    );
}
